import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import EmptyView from 'general/components/Others/EmptyView';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import Utils from 'general/utils/Utils';
import UIHelper from 'general/helpers/UIHelper';
import AgencyHelper from 'features/Agency/agencyHelper';
import AppColors from 'general/constants/AppColors';
import PropTypes from 'prop-types';

LandlordRevenueView.propTypes = {
    agencyId: PropTypes.string,
    landlordRevenueList: PropTypes.array,
    landlordRevenueTotal: PropTypes.number,
    onFilterChanged: PropTypes.func,
    filter: PropTypes.object,
    reconciliationDetail: PropTypes.object,
};

const defaultColumnIds = ['PREMISE_CODE', 'PREMISE_NAME', 'STATUS', 'ENERGY_CAR', 'ENERGY_BIKE', 'AMOUNT'];

function LandlordRevenueView(props) {
    // ---- params ----
    const router = useRouter();
    const { t } = useTranslation();
    const { agencyId, landlordRevenueList, landlordRevenueTotal, onFilterChanged, filter, reconciliationDetail } = props;
    const { user } = useSelector((state) => state.auth);

    const formatDataForTable = (landlordRevenues = []) => {
        const groupedByPremiseId = {};

        landlordRevenues.forEach((item) => {
            const { premiseId, name, status } = item;

            if (!groupedByPremiseId[premiseId]) {
                const chargingVolume = item?.totalEnergy || 0;
                const rentByEnergy = item?.totalEnergyCommission || 0;
                const rentFixed = item?.rentingType === 'FULL' ? (item?.monthlyRent || 0) : 0;
                const parkingSlotCount = item?.rentingType === 'SLOT' ? (item?.parkingSlots || 0) : 0;
                const parkingSlotPrice = item?.rentingType === 'SLOT' ? (item?.slotRent || 0) : 0;

                groupedByPremiseId[premiseId] = {
                    key: premiseId,
                    premiseCode: premiseId,
                    premiseName: name || '',
                    status: status || '',
                    chargingVolume,
                    rentByEnergy,
                    rentFixed,
                    parkingSlotCount,
                    parkingSlotPrice,
                    totalAmount: rentByEnergy + rentFixed + (parkingSlotCount * parkingSlotPrice),
                };
            }
        });

        return Object.values(groupedByPremiseId);
    };

    const formattedSaleKPIList = useMemo(() => {
        return formatDataForTable(landlordRevenueList);
    }, [landlordRevenueList]);

    const fullColumns = useMemo(() => {
        return [
            {
                title: UIHelper.buildTableColumn('Mã mặt bằng'),
                dataIndex: 'premiseCode',
                key: 'premiseCode',
                minWidth: 100,
                render: (value) => value || '',
            },
            {
                title: UIHelper.buildTableColumn('Tên mặt bằng'),
                dataIndex: 'premiseName',
                key: 'premiseName',
                minWidth: 230,
                render: (value) => value || '',
            },
            {
                title: UIHelper.buildTableColumn('Trạng thái hoạt động'),
                dataIndex: 'status',
                key: 'status',
                minWidth: 130,
                render: (value) => (
                    <KTLabel
                        text={AgencyHelper.Utils.getPremiseStatusName(value)}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${AgencyHelper.Utils.getPremiseStatusColor(value)}`}
                    />
                ),
            },
            {
                title: UIHelper.buildTableColumn('Sản lượng điện sạc', 'kWh'),
                dataIndex: 'chargingVolume',
                key: 'chargingVolume',
                minWidth: 180,
                align: 'right',
                render: (value) => Utils.formatNumber(value / 1000?.toFixed(3) ?? 0),
            },
            {
                title: UIHelper.buildTableColumn('Phí thuê theo doanh thu điện', 'VNĐ'),
                dataIndex: 'rentByEnergy',
                key: 'rentByEnergy',
                minWidth: 180,
                align: 'right',
                render: (value) => Utils.formatNumber(Math.round(value ?? 0)),
            },
            {
                title: UIHelper.buildTableColumn('Phí thuê cố định/tháng', 'VNĐ'),
                dataIndex: 'rentFixed',
                key: 'rentFixed',
                minWidth: 180,
                align: 'right',
                render: (value) => Utils.formatNumber(Math.round(value ?? 0)),
            },
            {
                title: UIHelper.buildTableColumn('Phí thuê theo chỗ đỗ xe'),
                key: 'parkingFeeGroup',
                children: [
                    {
                        title: UIHelper.buildTableColumn('Số chỗ đỗ xe'),
                        dataIndex: 'parkingSlotCount',
                        key: 'parkingSlotCount',
                        minWidth: 150,
                        align: 'right',
                        render: (value) => Utils.formatNumber(value ?? 0),
                        sorter: false,
                    },
                    {
                        title: UIHelper.buildTableColumn('Đơn giá/tháng'),
                        dataIndex: 'parkingSlotPrice',
                        key: 'parkingSlotPrice',
                        minWidth: 150,
                        align: 'right',
                        render: (value) => Utils.formatNumber(Math.round(value ?? 0)),
                        sorter: false,
                    },
                ],
            },
            {
                title: UIHelper.buildTableColumn('Thành tiền', 'VNĐ'),
                dataIndex: 'totalAmount',
                key: 'totalAmount',
                minWidth: 150,
                align: 'right',
                render: (value) => Utils.formatNumber(Math.round(value ?? 0)),
            },
        ];
    }, [t]);

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = `${router.pathname}${router.location.hash}`;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(fullColumns?.map((row) => row?.id).filter(Boolean));
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        saveChanges,
        onSelectAll,
        onUnSelectAll,
        fullColumnsIdSorted
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

    // ---- methods ----

    const handleFilterChanged = (filter) => {
        if (onFilterChanged) {
            onFilterChanged(filter);
        }
    };

    return (
        <div>
            <div>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>Tổng doanh thu/phí thuê mặt bằng:</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {' '} {Utils.formatNumber(Math.round(reconciliationDetail?.totalRevenue) ?? 0)} VNĐ
                </span>
            </div>
            {/* Body */}
            <div className="pt-4">
                <KTTable
                    columns={columns}
                    items={formattedSaleKPIList}
                    selectableRows={false}
                    striped={false}
                    compactGrid={false}
                    onClickedRow={() => { }}
                    onSelectedRowsChanged={(rows) => { }}
                    onDoubleClickedRow={(rows) => { }}
                    hasActionColumn={false}
                    groupHeader={true}
                    noDataComponent={
                        <EmptyView
                            icon={imgEmptyNoData}
                            title="Không có dữ liệu để hiển thị"
                            text={
                                <div className="text-center">
                                    <span>
                                        Hiện không có dữ liệu để hiển thị. Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.
                                    </span>
                                </div>
                            }
                        />
                    }
                />
            </div>

            {/* Phân trang */}
            {!_.isEmpty(landlordRevenueList) && (
                <div className="mt-4">
                    <KTPagination
                        currentPage={filter.page + 1}
                        rowCount={landlordRevenueTotal}
                        rowsPerPage={filter.limit}
                        currentItemInPageCount={landlordRevenueList.length}
                        onChangePage={(newPage) => {
                            const iNewPage = parseInt(newPage);
                            handleFilterChanged({
                                page: iNewPage - 1,
                            });
                        }}
                        onChangeRowsPerPage={(newRowsPerPage) => {
                            const iNewRowsPerPage = parseInt(newRowsPerPage);
                            handleFilterChanged({
                                limit: iNewRowsPerPage,
                                page: 0,
                            });
                        }}
                        loading={false}
                        color={KTPaginationColor.success}
                        appendText="mặt bằng"
                    />
                </div>
            )}
        </div>
    );
}

export default LandlordRevenueView;
